import { useContext } from "react";
import { instanceOf, node } from "prop-types";
import { action, observable } from "mobx";
import { BreakdownColumns, SectionFormats, TaskTypes } from "@repo/utils";
import { DEFAULT_BREAKDOWN_COLUMNS, StructureModel, blankStructureGenerator } from "@repo/structure";
import {
  fetchLibraryQuery,
  getCompanySettingsQuery,
  initLibraryQuery,
  setCompanySettingsQuery,
} from "@query";
import { COMPANY_SETTINGS } from "@client";
import { LIBRARY_LEVELS } from "@utils";
import { LibraryStoreContext } from "./LibraryStoreContext";

// =================== Context, Provider & Hook ===================
export const LibraryStoreProvider = ({ children, value }) => (
  <LibraryStoreContext.Provider value={value}>
    {children}
  </LibraryStoreContext.Provider>
);

export const useLibraryStore = () => {
  const context = useContext(LibraryStoreContext);
  if (context === null) {
    throw new Error("LibraryStoreContext cannot be null, please add a context provider");
  }
  return context;
};


// =================== Store ===================
export class LibraryStore {
  constructor({
    defaultCurrency
  }) {
    this.defaultCurrency = defaultCurrency;
  }

  @observable accessor hasSuggestions = false;
  @observable accessor pending = false;
  @action setPendingState = (state) => (this.pending = state);

  defaultCurrency = undefined;
  libraryWorkTypes = undefined;

  @action
  async initStructure(workTypes) {
    let root = await fetchLibraryQuery(this.pending);
    if (!root?.module?.length && !this.pending) {
      await initLibraryQuery();
      root = await fetchLibraryQuery(this.pending);
    }

    let libraryWorkTypes = (
      await getCompanySettingsQuery(COMPANY_SETTINGS.LIBRARY_WT)
    )?.filter((id) => id);

    let librarySettings = this.librarySettings || (await this.getLibrarySettings());

    if (!libraryWorkTypes?.length) {
      libraryWorkTypes = [2, 3, 5, 6];
      await this.setLibraryWorkTypes(libraryWorkTypes);
    }
    
    const priceFormat = await getCompanySettingsQuery(COMPANY_SETTINGS.PRICE_FORMAT_DEF);
    const defaultTimeEquiv = await getCompanySettingsQuery(COMPANY_SETTINGS.TIME_EQUIV);

    const existingLibraryWorkTypes = libraryWorkTypes.filter((id) =>
      workTypes.find((tag) => tag.id === id)
    );

    const structure = blankStructureGenerator({
      workTypes: workTypes.filter(wT => existingLibraryWorkTypes.includes(wT.id)),
      defaultCurrency: this.defaultCurrency,
      defaultCompanyCurrency: this.defaultCurrency,
      defaultTimeEquivalents: defaultTimeEquiv,
      defaultTimeModifier: librarySettings?.timeModifier > 1 ? 0 : librarySettings?.timeModifier,
      defaultPriceFormat: priceFormat,
      defaultSectionFormat: SectionFormats.SIMPLE,
      defaultMinMax: librarySettings?.useMinMax,
      // noPrices: !librarySettings?.showPrices,
      noSections: true,
    });
    
    structure.sections = LIBRARY_LEVELS
      .filter((level) => !this.pending || root[level]?.length)
      .map((level, index) => ({
        id: `${level}Collection`,
        type: TaskTypes.WORKLOAD,
        lvl: index,
        name: level,
        startingLevel: index + 1,
        status: 1,
        children: root[level] || [],
        workTypes: existingLibraryWorkTypes.map((id) => ({ id })),
        libraryTask: true,
        isOpened: true,
        usedBreakdownColumns: DEFAULT_BREAKDOWN_COLUMNS
          .filter(({ name }) => name !== BreakdownColumns.LABELS && name !== BreakdownColumns.NUMERATION)
          .map((column) => ({
            ...column,
            visible: librarySettings && librarySettings.columns
              ? librarySettings.columns.includes(column.name)
              : column.visible,
          })),
      }));
    
    return StructureModel.create(structure);
  }

  @action async setLibraryWorkTypes(value) {
    if (!value) return;
    if (value.join(",") !== this.libraryWorkTypes?.join(",")) {
      this.libraryWorkTypes = value;
      return await setCompanySettingsQuery(COMPANY_SETTINGS.LIBRARY_WT, value);
    }
  }

  @action async getLibrarySettings() {
    return (
      this.librarySettings ||
      (this.librarySettings = await getCompanySettingsQuery(
        COMPANY_SETTINGS.LIBRARY_SETTINGS
      ))
    );
  }

  @action async setLibrarySettings(settings) {
    return await setCompanySettingsQuery(
      COMPANY_SETTINGS.LIBRARY_SETTINGS,
      settings
    );
  }
}

LibraryStoreProvider.propTypes = {
  children: node.isRequired,
  value: instanceOf(LibraryStore).isRequired,
};
